import React, { useMemo } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListSkeleton, SectionCard, StatusChip } from '../../components/ui';
import type { RegistryResponse } from '../../components/adminFeatureFlags/featureFlagTypes';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { SignalTone, tokens } from '../../theme/tokens';
import { systemJson } from './api';
import type { SystemV2Tab } from './legacyTabs';
import { ActivityAuditEntry, buildActivity, settingsEvent } from './systemActivity';

interface SupportContactsResponse {
  settings: { dedicated: { name: string }; updatedAt?: unknown; updatedBy?: string } | null;
  preview: { name: string };
}

interface BhSettings {
  settings: { maxConcurrentFills: number; updatedAt?: unknown; updatedBy?: string };
}

interface ReleaseList {
  steps: Array<{ id: string; title: string; lastApply: unknown }>;
}

interface NormStatus {
  collections: Record<string, { total: number; processed: number; pending: number }>;
}

/** Same key and payload as the Feature flags tab, so opening it reuses this read. */
const FLAG_REGISTRY_KEY = ['admin-feature-flags-registry'] as const;

interface OverviewCard {
  key: SystemV2Tab;
  title: string;
  stat: string;
  detail: string;
  cta: string;
  tone: SignalTone;
  statTone?: SignalTone;
}

export const OverviewTab: React.FC<{
  onOpen: (tab: SystemV2Tab) => void;
  requestsOpen: number;
  privacyUrgent: boolean;
  settingsDirty: boolean;
  unappliedScripts: number | null;
}> = ({ onOpen, requestsOpen, privacyUrgent, settingsDirty, unappliedScripts }) => {
  const { t } = useAppTranslation();
  // Most system routes record resource type `admin`; normalization runs record
  // `canonical_product`, so the unfiltered read catches those beside product edits.
  const adminActivity = useQuery({
    queryKey: ['admin-system-activity', 'admin'],
    queryFn: () => systemJson<{ logs: ActivityAuditEntry[] }>('/admin/audit-logs?resourceType=admin&limit=100'),
  });
  const recentActivity = useQuery({
    queryKey: ['admin-system-activity', 'recent'],
    queryFn: () => systemJson<{ logs: ActivityAuditEntry[] }>('/admin/audit-logs?limit=100'),
  });
  const support = useQuery({
    queryKey: ['admin-support-contacts'],
    queryFn: () => systemJson<SupportContactsResponse>('/billing/admin/support-contacts'),
  });
  const fills = useQuery({
    queryKey: ['admin-bh-fill-settings'],
    queryFn: () => systemJson<BhSettings>('/ingestion/bh-fill-settings'),
  });
  const release = useQuery({
    queryKey: ['admin-release-steps'],
    queryFn: () => systemJson<ReleaseList>('/admin/release-steps'),
  });
  const norm = useQuery({
    queryKey: ['admin-normalization-status'],
    queryFn: () => systemJson<NormStatus>('/normalization/status'),
  });
  const flags = useQuery({
    queryKey: FLAG_REGISTRY_KEY,
    queryFn: () => systemJson<RegistryResponse>('/admin/feature-flags'),
    staleTime: 60_000,
  });

  const releaseTitles = useMemo(() => {
    const map: Record<string, string> = {};
    for (const step of release.data?.steps || []) map[step.id] = step.title;
    return map;
  }, [release.data]);

  const events = useMemo(
    () =>
      buildActivity(
        [...(adminActivity.data?.logs || []), ...(recentActivity.data?.logs || [])],
        [
          settingsEvent('settings:support', 'supportSaved', support.data?.settings?.updatedAt, support.data?.settings?.updatedBy),
          settingsEvent('settings:fills', 'fillsSaved', fills.data?.settings.updatedAt, fills.data?.settings.updatedBy, {
            count: fills.data?.settings.maxConcurrentFills ?? 1,
          }),
        ],
        releaseTitles,
      ),
    [adminActivity.data, recentActivity.data, support.data, fills.data, releaseTitles],
  );
  const activityLoading = adminActivity.isLoading || recentActivity.isLoading;
  const activityFailed = adminActivity.isError && recentActivity.isError;

  const releaseTotal = release.data?.steps.length ?? null;
  const pendingNorm = norm.data ? Object.values(norm.data.collections).reduce((sum, item) => sum + item.pending, 0) : null;
  const flagSummary = useMemo(() => {
    if (!flags.data) return null;
    let on = 0;
    let off = 0;
    let plan = 0;
    let overridden = 0;
    for (const def of flags.data.definitions) {
      const global = def.globalOverride ?? flags.data.global?.[def.key] ?? null;
      if (global === 'plan') plan += 1;
      else if (global === true || (global === null && def.defaultEnabled)) on += 1;
      else off += 1;
      if ((def.overrideCount ?? def.overrides?.length ?? 0) > 0) overridden += 1;
    }
    return { total: flags.data.definitions.length, on, off, plan, overridden };
  }, [flags.data]);

  const dedicatedName = support.data?.settings?.dedicated.name || support.data?.preview.name || '';
  const fillsAtOnce = fills.data?.settings.maxConcurrentFills;

  const cards: OverviewCard[] = [
    {
      key: 'requests',
      title: t('admin.systemV2.tabs.requests'),
      stat: requestsOpen ? t('admin.systemV2.overview.requestsOpen', { count: requestsOpen }) : t('admin.systemV2.overview.allClear'),
      statTone: requestsOpen ? undefined : 'positive',
      detail: requestsOpen
        ? privacyUrgent
          ? t('admin.systemV2.overview.requestsUrgent')
          : t('admin.systemV2.overview.requestsDetail')
        : t('admin.systemV2.overview.requestsNone'),
      cta: t('admin.systemV2.overview.ctaRequests'),
      tone: privacyUrgent ? 'problem' : requestsOpen > 0 ? 'caution' : 'positive',
    },
    {
      key: 'scripts',
      title: t('admin.systemV2.tabs.scripts'),
      stat:
        releaseTotal === null || unappliedScripts === null
          ? t('admin.systemV2.overview.scriptsUnknown')
          : t('admin.systemV2.overview.scriptsStat', { applied: releaseTotal - unappliedScripts, total: releaseTotal }),
      detail: [
        unappliedScripts ? t('admin.systemV2.overview.scriptsWaiting', { count: unappliedScripts }) : null,
        pendingNorm !== null ? t('admin.systemV2.overview.normPending', { count: pendingNorm, formatted: pendingNorm.toLocaleString() }) : null,
      ]
        .filter(Boolean)
        .join(' · ') || t('admin.systemV2.overview.scriptsDetail'),
      cta: t('admin.systemV2.overview.ctaScripts'),
      tone: unappliedScripts ? 'caution' : unappliedScripts === null ? 'neutral' : 'positive',
    },
    {
      key: 'flags',
      title: t('admin.systemV2.tabs.flags'),
      stat: flagSummary ? t('admin.systemV2.overview.flagsStat', { count: flagSummary.total }) : t('admin.systemV2.tabs.flags'),
      detail: flagSummary
        ? t('admin.systemV2.overview.flagsDetail', flagSummary)
        : t('admin.systemV2.overview.flags'),
      cta: t('admin.systemV2.overview.ctaFlags'),
      tone: 'neutral',
    },
    {
      key: 'settings',
      title: t('admin.systemV2.tabs.settings'),
      stat: settingsDirty ? t('admin.systemV2.overview.settingsDirty') : dedicatedName || t('admin.systemV2.tabs.settings'),
      detail: fillsAtOnce !== undefined
        ? t('admin.systemV2.overview.settingsDetail', { count: fillsAtOnce })
        : t('admin.systemV2.overview.settingsClean'),
      cta: t('admin.systemV2.overview.ctaSettings'),
      tone: settingsDirty ? 'caution' : 'neutral',
    },
    {
      key: 'repair',
      title: t('admin.systemV2.tabs.repair'),
      stat: t('admin.systemV2.overview.repair'),
      detail: t('admin.systemV2.overview.repairDetail'),
      cta: t('admin.systemV2.overview.ctaRepair'),
      tone: 'neutral',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
          gap: 1.75,
        }}
      >
        {cards.map((card) => (
          <SectionCard
            key={card.key}
            dense
            padded
            sx={{ display: 'flex', flexDirection: 'column', '& > .MuiBox-root': { flex: 1, display: 'flex', flexDirection: 'column' } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.body }}>{card.title}</Typography>
                <Box
                  component="span"
                  aria-hidden
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.signal[card.tone].solid, flex: '0 0 auto' }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    overflowWrap: 'anywhere',
                    color: card.statTone ? tokens.signal[card.statTone].ink : tokens.ink.primary,
                  }}
                >
                  {card.stat}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.375 }}>{card.detail}</Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Button variant="outlined" size="small" onClick={() => onOpen(card.key)} sx={{ alignSelf: 'flex-start' }}>
                {card.cta}
              </Button>
            </Box>
          </SectionCard>
        ))}
      </Box>

      <SectionCard
        dense
        title={t('admin.systemV2.overview.activity')}
        actions={
          <Link component={RouterLink} to="/app/admin/audit-logs" underline="hover" sx={{ fontSize: 12.5, fontWeight: 600 }}>
            {t('admin.systemV2.overview.auditLog')}
          </Link>
        }
      >
        {activityLoading ? (
          <ListSkeleton rows={4} />
        ) : activityFailed ? (
          <Typography sx={{ fontSize: 13.5, color: tokens.signal.problem.ink, px: '20px', py: 2 }}>{t('admin.systemV2.overview.activityError')}</Typography>
        ) : events.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, px: '20px', py: 2 }}>{t('admin.systemV2.overview.activityEmpty')}</Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {events.map((event) => (
              <Box
                key={event.id}
                component="li"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '110px minmax(0, 1fr) auto' },
                  gap: { xs: 0.5, sm: 1.75 },
                  alignItems: 'center',
                  px: '20px',
                  py: '11px',
                  borderTop: `1px solid ${tokens.line.hairline}`,
                  '&:first-of-type': { borderTop: 0 },
                }}
              >
                <StatusChip
                  tone={event.tone}
                  dot
                  label={t(`admin.systemV2.activity.areas.${event.area}`)}
                  sx={{ justifySelf: 'start' }}
                />
                <Typography sx={{ fontSize: 13, color: tokens.ink.primary, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {t(`admin.systemV2.activity.events.${event.key}`, event.params)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.ink.faint, whiteSpace: { sm: 'nowrap' } }}>
                  {[event.at?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }), event.actor]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};
